import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Navbar from '../components/homepage/Navbar';
import HeroSection from '../components/homepage/HeroSection';
import Contact from '../components/homepage/Contact';
import Footer from '../components/homepage/Footer';
import { Box, Typography, useMediaQuery, useTheme, InputAdornment, TextField } from '@mui/material';
import { fetchAllEmployees, fetchServices, getCurrentBusinessConfig, getImageFromStorage, getAppointmentById } from '../firebase/firebase-delegate';
import ServicesSection from '../components/employee/services/ServicesSection';
import { Dialog, DialogContent, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Appointment, BookingConfirmation, BusinessConfig, Service } from '../types';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import LoadingSpinner from '../components/global/LoadingSpinner';
import { motion } from 'framer-motion';
import GallerySection from './Admin/Gallery/GalleryHomepageSection';
import BusinessHoursCard from '../components/homepage/BusinessHoursCard';
import UnderConstructionPage from './UnderConstructionPage';
import { ConfirmationNumberOutlined } from '@mui/icons-material';
import { ScrollFadeIn } from '../components/homepage/utils/Utils';
import StaffSection, { EmployeeWithPhoto } from '../components/homepage/StaffSection';
import ReviewsSection from '../components/homepage/ReviewsSection';
import { getCachedAssetUrl } from '../utils/assetCache';
import { useLocation } from 'react-router-dom';
import { isSubscriptionAccessible } from '../utils/subscriptionAccess';
import { resolveBookingUxConfig } from '../utils/bookingUxConfig';
import {
  RecentBookingSnapshot,
  buildAppointmentFromSnapshot,
  buildRecentBookingSnapshot,
  isWithinOneHourOfBooking,
  loadRecentBookingSnapshot,
  saveRecentBookingSnapshot,
} from '../utils/recentBookingUtils';

// Lazy-load the heavy booking dialog bundle only when the user opens it.
const BookNowDialog = lazy(() => import('../components/booking/BookNowDialog'));

const Homepage: React.FC = () => {
  const location = useLocation();
  const [openBooking, setOpenBooking] = useState(false); // Modal state for Booking
  const [logoUrl, setLogoUrl] = useState<string | null>(null); // Logo image URL
  const [heroLogoUrl, setHeroLogoUrl] = useState<string | null>(null);

  const [heroImageURL, setHeroImageURL] = useState<string | null>(null);
  const [assetsReady, setAssetsReady] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [recentBookingData, setRecentBookingData] = useState<RecentBookingSnapshot | null>(null);
  const [bookingStartStep, setBookingStartStep] = useState<number | null>(null);

  const [appointmentIdInput, setAppointmentIdInput] = useState('');
  const [appointmentIdError, setAppointmentIdError] = useState<string | null>(null);

  const handleGoToAppointment = async () => {
    const id = appointmentIdInput.trim();
    if (!id) {
      setAppointmentIdError('Please enter your appointment ID.');
      return;
    }
    try {
      await getAppointmentById(id);
    } catch {
      setAppointmentIdError('Sorry, we could not find that appointment.');
      return;
    }
    // Navigate relative to current site
    window.location.assign(`/myappointment?id=${encodeURIComponent(id)}`);
  };

  const servicesQuery = useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: () => fetchServices(),
  });

  const employeesQuery = useQuery<EmployeeWithPhoto[]>({
    queryKey: ['employees'],
    queryFn: async () => {
      const employees = await fetchAllEmployees();
      return Promise.all(
        employees.map(async (employee) => {
          const picturePath = employee.profilePictureImagePath;
          if (!picturePath) {
            return employee;
          }

          try {
            const url = await getCachedAssetUrl(
              picturePath,
              () => getImageFromStorage(picturePath)
            );
            if (!url) {
              return employee;
            }
            return { ...employee, profilePictureUrl: url };
          } catch (error) {
            console.error('Failed to load employee profile picture', error);
            return employee;
          }
        })
      );
    },
  });

  const servicesWithImages = (servicesQuery.data ?? []).filter((service) => service.imagePath);
  const publicBookableEmployeeIds = useMemo(() => {
    const ids = new Set<string>();
    (servicesQuery.data ?? []).forEach((service) => {
      (service.employees ?? []).forEach((employeeId) => {
        if (employeeId) {
          ids.add(employeeId);
        }
      });
    });
    return ids;
  }, [servicesQuery.data]);
  const publicStaff = useMemo(
    () => (employeesQuery.data ?? []).filter(
      (employee) => employee.active !== false && publicBookableEmployeeIds.has(employee.id)
    ),
    [employeesQuery.data, publicBookableEmployeeIds],
  );
  const showServicesLink = servicesWithImages.length > 0;
  const isBookNowRoute = location.pathname === '/book-now';
  const preferredEmployeeId = (() => {
    const employeeId = new URLSearchParams(location.search).get('employee')?.trim();
    return employeeId || undefined;
  })();

  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });

  const config = businessConfigQuery.data;
  const bookingUxConfig = useMemo(
    () => resolveBookingUxConfig(businessConfigQuery.data),
    [businessConfigQuery.data]
  );
  useEffect(() => {
    if (!businessConfigQuery.data) return;

    let isCancelled = false;

    const preloadImage = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve();
        img.onerror = () =>
          reject(new Error('Failed to preload image.'));
      });

    // Wrap getDownloadURL so we reuse signed URLs and let the browser HTTP cache handle bytes.
    const fetchAsset = async (path?: string | null) => {
      if (!path) {
        return null;
      }
      try {
        const cachedUrl = await getCachedAssetUrl(path, async () => {
          const downloadUrl = await getDownloadURL(ref(storage, path));
          return downloadUrl;
        });

        if (cachedUrl) {
          await preloadImage(cachedUrl);
        }

        return cachedUrl;
      } catch (error) {
        console.error('Failed to load asset:', error);
        return null;
      }
    };

    const loadImages = async () => {
      const heroImagePath = businessConfigQuery.data?.homepage?.heroImagePath;
      const logoImagePath = businessConfigQuery.data?.businessInfo?.logoImagePath;
      const heroLogoImagePath = businessConfigQuery.data?.homepage?.heroLogoImagePath;

      // Fire all downloads in parallel and block render until each asset is ready.
      const [heroImage, navbarLogo, heroLogo] = await Promise.all([
        fetchAsset(heroImagePath),
        fetchAsset(logoImagePath),
        fetchAsset(heroLogoImagePath),
      ]);

      if (!isCancelled) {
        setHeroImageURL(heroImage);
        setLogoUrl(navbarLogo);
        setHeroLogoUrl(heroLogo);
      }
    };

    setAssetsReady(false);
    loadImages()
      .catch((error) => {
        console.error('Failed to load homepage assets:', error);
      })
      .finally(() => {
        if (!isCancelled) {
          setAssetsReady(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [businessConfigQuery.data]);

  const handleAppointment = (appointment: Appointment | null) => {
    setAppointment(appointment);
  };

  useEffect(() => {
    setRecentBookingData(loadRecentBookingSnapshot());
  }, []);

  useEffect(() => {
    if (!isBookNowRoute) {
      return;
    }
    if (!servicesQuery.isSuccess || !employeesQuery.isSuccess || !businessConfigQuery.isSuccess) {
      return;
    }

    setAppointment(null);
    setBookingStartStep(null);
    setOpenBooking(true);
  }, [
    isBookNowRoute,
    preferredEmployeeId,
    servicesQuery.isSuccess,
    employeesQuery.isSuccess,
    businessConfigQuery.isSuccess,
  ]);

  const handleBookingSuccess = (booking: BookingConfirmation) => {
    const snapshot = buildRecentBookingSnapshot(booking);
    saveRecentBookingSnapshot(snapshot);
    setRecentBookingData(snapshot);
  };

  const isLoading =
    servicesQuery.isLoading ||
    servicesQuery.isFetching ||
    businessConfigQuery.isLoading ||
    employeesQuery.isLoading ||
    businessConfigQuery.isFetching ||
    (Boolean(businessConfigQuery.data) && !assetsReady);
  const isError = servicesQuery.isError || businessConfigQuery.isError || employeesQuery.isError || !businessConfigQuery.data;

  const theme = useTheme();
  // Determine if the screen is mobile, if yes, then show booking dialog in fullscreen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenBooking = () => {
    setOpenBooking(true);
    setAppointment(null);
    setBookingStartStep(null);
  };
  const handleBookAgain = () => {
    if (!recentBookingData) return;
    const draftAppointment = buildAppointmentFromSnapshot(recentBookingData, servicesQuery.data ?? []);
    setAppointment(draftAppointment);
    setBookingStartStep(2);
    setOpenBooking(true);
  };
  const handleCloseBooking = () => {
    setOpenBooking(false); // Close modal
    setAppointment(null);
    setBookingStartStep(null);
  };
  const shouldShowRebookPrompt =
    Boolean(recentBookingData) && !isWithinOneHourOfBooking(recentBookingData);

  if (isLoading) return <LoadingSpinner />;

  const paymentInfo = config?.paymentInfo;
  const hasSubscriptionAccess = isSubscriptionAccessible(paymentInfo);
  const logoPlacement = config?.homepage?.logoPlacement ?? 'navbar';
  const navbarLogoUrl = (logoPlacement === 'navbar' || logoPlacement === 'both') ? logoUrl : null;
  const heroOverlayLogoUrl = (logoPlacement === 'heroOverlay' || logoPlacement === 'both') ? heroLogoUrl : null;
  const aboutUsContent = config?.homepage?.aboutUs?.trim();
  const showHomepageGallery =
    bookingUxConfig.uiFlags.showHomepageGallerySection &&
    (businessConfigQuery.data?.homepage?.gallery ?? []).length > 0;
  const showHomepageTeam = bookingUxConfig.uiFlags.showHomepageTeamSection;
  const showHomepageReviews = bookingUxConfig.uiFlags.showHomepageReviewsSection;
  const showHomepageAbout = bookingUxConfig.uiFlags.showHomepageAboutSection && Boolean(aboutUsContent);
  const homepageResourcePluralLabel = bookingUxConfig.terminology.resourcePlural;

  if (
    !config ||
    !config.businessInfo?.name ||
    !config.homepage?.heroTitle ||
    !hasSubscriptionAccess
  ) {
    return <UnderConstructionPage />;
  }

  if (isError) return <Typography color="error">Failed to load</Typography>;


  return (
    <>
      <Navbar
        onOpenBooking={handleOpenBooking}
        logoUrl={navbarLogoUrl}
        businessInfo={businessConfigQuery.data!.businessInfo}
        logoPlacement={logoPlacement}
        showServicesLink={showServicesLink}
      />

      {/* Hero Section with fade-in */}
      <div id="hero">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroSection
            onOpenBooking={handleOpenBooking}
            onBookAgain={handleBookAgain}
            showBookAgain={shouldShowRebookPrompt}
            homepageInfo={businessConfigQuery.data!.homepage}
            heroImageURL={heroImageURL}
            logoPlacement={logoPlacement}
            logoUrl={heroOverlayLogoUrl}
            businessName={businessConfigQuery.data!.businessInfo.name}
          />
        </motion.div>
      </div>

      {/* Services Section with scroll-in animation */}
      <div id="services">
        <ScrollFadeIn>
          <ServicesSection services={servicesQuery.data ?? []} />
        </ScrollFadeIn>
      </div>

      {showHomepageGallery && (
        <Box id="gallery" sx={{ mt: { xs: 2, md: 2 } }}>
          <ScrollFadeIn>
            <GallerySection gallery={businessConfigQuery.data?.homepage?.gallery ?? []} />
          </ScrollFadeIn>
        </Box>
      )}


      {/* About Us Section */}
      {showHomepageTeam && (
        <StaffSection
          employees={publicStaff}
          showBio={bookingUxConfig.uiFlags.showEmployeeBio}
          showSocials={bookingUxConfig.uiFlags.showEmployeeSocials}
          showProfilePhoto={bookingUxConfig.uiFlags.showEmployeeProfilePhoto}
          resourcePluralLabel={homepageResourcePluralLabel}
        />
      )}

      {showHomepageReviews && <ReviewsSection reviews={businessConfigQuery.data?.homepage?.reviews} />}

      {showHomepageAbout && (
        <Box id="about-us" px={2} mt={{ xs: 2, md: 2 }} mb={{ xs: 2, md: 2 }} style={{ backgroundColor: theme.palette.background.default }}>
          <ScrollFadeIn>
            <Box maxWidth="md" mx="auto" textAlign="center">
              <Typography variant="h4" gutterBottom fontWeight={600}>
                About Us
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                {aboutUsContent}
              </Typography>
            </Box>
          </ScrollFadeIn>
        </Box>
      )}

      {/* Business Hours Section */}
      <Box id="hours" px={2} mt={{ xs: 2, md: 2 }} mb={{ xs: 2, md: 2 }} sx={{ backgroundColor: theme.palette.background.default }}>
        <ScrollFadeIn>
          <Box maxWidth="md" mx="auto" textAlign="center">
            <BusinessHoursCard hours={businessConfigQuery.data!.businessInfo.businessHours} />
          </Box>
        </ScrollFadeIn>
      </Box>

      <Box id="manage-appointment" px={2} mt={{ xs: 2, md: 2 }} mb={{ xs: 2, md: 2 }} sx={{ backgroundColor: theme.palette.background.default }}>
        <Box maxWidth="md" mx="auto" textAlign="center">
          {/* Animate once on first scroll into view; never again */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Find My Appointment
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 2 }}
            >
              Enter your Appointment ID to view, reschedule, or cancel.
            </Typography>
          </motion.div>

          <Box maxWidth={600} mx="auto">
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleGoToAppointment();
              }}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'stretch',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Appointment ID"
                placeholder="e.g. ABC1234"
                value={appointmentIdInput}
                onChange={(e) => {
                  setAppointmentIdInput(e.target.value);
                  setAppointmentIdError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleGoToAppointment();
                }}
                error={!!appointmentIdError}
                helperText={appointmentIdError ?? ' '}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ConfirmationNumberOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiFilledInput-root': {
                    borderRadius: '20px',
                    backgroundColor: (t) =>
                      t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'white',
                  },
                }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToAppointment}
                sx={{
                  px: 3,
                  height: { xs: 48, sm: 56 },
                  borderRadius: '20px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: 'common.white',
                  '&:hover': {
                    color: 'common.white',
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>


      {/* Contact + Footer */}
      < div id="contact" >
        <Contact businessConfig={businessConfigQuery.data!} />
      </div >
      <Footer />

      {/* Booking Modal */}
      {openBooking && (
        <Dialog
          open
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseBooking();
            }
          }}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          sx={{
            height: isMobile ? '100%' : '95%',
            '& .MuiDialog-paper': {
              height: isMobile ? '100%' : '95%',
              maxHeight: 'none',
              margin: isMobile ? 0 : 'auto',
            },
          }}
        >
          <IconButton
            onClick={handleCloseBooking}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              color: '#000',
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent sx={{ padding: '0 20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Suspense fallback={<LoadingSpinner />}>
              <BookNowDialog appointment={appointment}
                handleAppointment={handleAppointment}
                services={servicesQuery.data ?? []}
                handleCloseBooking={handleCloseBooking}
                enabledCustomerSMS={businessConfigQuery.data?.features.enableCustomerSMS ?? false}
                enabledEmployeeSMS={businessConfigQuery.data?.features.enableEmployeeSMS ?? false}
                enabledPayments={businessConfigQuery.data?.features.enablePayments ?? false}
                requirePaymentForBooking={businessConfigQuery.data?.preferences?.requirePaymentForBooking ?? false}
                minimumBookingNoticeHours={businessConfigQuery.data?.preferences?.minimumBookingNoticeHours ?? 1}
                onBookingSuccess={handleBookingSuccess}
                prefillCustomer={recentBookingData ? {
                  firstName: recentBookingData.customerFirstName,
                  lastName: recentBookingData.customerLastName,
                  email: recentBookingData.customerEmail,
                  phone: recentBookingData.customerPhoneNumber,
                } : null}
                preferredEmployeeId={
                  bookingUxConfig.bookingModel === 'resource'
                    ? undefined
                    : preferredEmployeeId
                }
                initialStep={bookingStartStep ?? undefined}
              />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}
    </>

  );
};

export default Homepage;
